import {
  type ColumnDef,
  type ColumnSizingState,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from '@tanstack/react-table';
import React, { useCallback, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router';

import { useTheme } from '@mui/material';
import Box from '@mui/material/Box';
import Checkbox from '@mui/material/Checkbox';
import Paper from '@mui/material/Paper';
import MUITable from '@mui/material/Table';
import MUITableBody from '@mui/material/TableBody';
import MUITableCell from '@mui/material/TableCell';
import MUITableContainer from '@mui/material/TableContainer';
import MUITableHead from '@mui/material/TableHead';
import MUITableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';

import { IconButton } from '@components/button';
import { Loader } from '@components/loader';
import { Skeleton } from '@components/skeleton';
import { mergeSx } from '@assets/helpers/utils/merge-sx.ts';
import { RefreshIcon } from '@assets/icons/refresh.tsx';

import {
  areColumnSizingEqual,
  clampColumnSize,
  clampValue,
  DEFAULT_COLUMN_SIZE,
  DEFAULT_MAX_COLUMN_SIZE,
  DEFAULT_MIN_COLUMN_SIZE,
  DEFAULT_SCROLL_METRICS,
  getColumnCurrentSize,
  getColumnLayoutSignature,
  getColumnMaxSize,
  getColumnMinSize,
  getColumnSizeVars,
  getColumnTotalSize,
  getFilledColumnSizing,
  getScrollableDistance,
  getScrollbarThumbMetrics,
  getScrollPositionFromTrackPointer,
  getStorageKey,
  getStoredColumnSizing,
  hasScrollableOverflow,
  normalizeColumnSizing,
  type ResizableTableScrollMetrics,
  setStoredColumnSizing,
} from './helpers.ts';
import {
  resizableTableBodySx,
  resizableTableCellSx,
  resizableTableContainerSx,
  resizableTableDividerCellSx,
  resizableTableDividerSx,
  resizableTableEmptyStateSx,
  resizableTableFullWidthCellSx,
  resizableTableHeadBackdropSx,
  resizableTableHeadCellSx,
  resizableTableHeadContentSx,
  resizableTableHeadRowSx,
  resizableTableHeadSx,
  resizableTableLinkedRowSx,
  resizableTableLoaderCellSx,
  resizableTablePaperSx,
  resizableTablePinnedSubRowContentSx,
  resizableTableResizeHandleSx,
  resizableTableRootSx,
  resizableTableRowLinkSx,
  resizableTableRowSx,
  resizableTableScrollbarCornerSx,
  resizableTableScrollbarFooterSx,
  resizableTableScrollbarSlotSx,
  resizableTableScrollbarThumbClassName,
  resizableTableScrollbarThumbSx,
  resizableTableScrollbarTrackClassName,
  resizableTableScrollbarTrackSx,
  resizableTableScrollContentRowSx,
  resizableTableScrollLayoutSx,
  resizableTableVerticalScrollbarColumnSx,
  resizableTableViewportSx,
} from './styles.ts';
import type { ResizableTableColumn, ResizableTableProps } from './types.ts';

const TRAILING_FILL_COLUMN_SIZE_CSS_VARIABLE = '--trailing-fill-column-size';
const trailingFillColumnWidthStyle = `calc(var(${TRAILING_FILL_COLUMN_SIZE_CSS_VARIABLE}) * 1px)`;

type CustomScrollbarAxis = 'vertical' | 'horizontal';

type ScrollbarVisibility = {
  vertical: boolean;
  horizontal: boolean;
};

const DEFAULT_SCROLLBAR_VISIBILITY: ScrollbarVisibility = {
  vertical: false,
  horizontal: false,
};

const MULTI_SELECT_COLUMN_ID = '__multiSelect__';
const MULTI_SELECT_COLUMN_SIZE = 56;
const DEFAULT_ROWS_SKELETON_COUNT = 5;
const DEFAULT_SKELETON_HEIGHT = 24;

const renderHeaderContent = (content: React.ReactNode) => {
  if (typeof content !== 'string' && typeof content !== 'number') {
    return (
      <Typography noWrap variant={'body_14_24'}>
        {content}
      </Typography>
    );
  }

  return String(content)
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .map((part, index) => (
      <Typography noWrap key={`${part}-${index}`} variant={'body_14_24'}>
        {part}
      </Typography>
    ));
};

export const ResizableTable = <TData,>(props: ResizableTableProps<TData>) => {
  const {
    columns,
    data,
    emptyNode,
    fillThePage = false,
    getRowId,
    getRowProps,
    getRowLink,
    infiniteLoader,
    isLoading = false,
    maxHeight,
    multiSelect,
    onRowClick,
    paperProps,
    pinSubRowToContainer = false,
    renderSubRow,
    stickyHeader = false,
    skeletonHeight = DEFAULT_SKELETON_HEIGHT,
    tableContainerProps,
    tableProps,
    tableStorageId,
    rowsSkeletonCount = DEFAULT_ROWS_SKELETON_COUNT,
    withRowDividers = true,
  } = props;

  const location = useLocation();
  const theme = useTheme();
  const multiSelectCheckboxSx = useMemo(
    () => ({
      color: theme.palette.dark,
      '&.Mui-checked, &.MuiCheckbox-indeterminate': {
        color: theme.palette.accent,
      },
    }),
    [theme.palette.accent, theme.palette.semi_dark]
  );
  const multiSelectColumn = useMemo<ResizableTableColumn<TData> | null>(() => {
    if (!multiSelect?.isMultiSelectEnabled) {
      return null;
    }

    return {
      id: MULTI_SELECT_COLUMN_ID,
      header: (
        <Checkbox
          size={'small'}
          checked={multiSelect.isAllSelected}
          indeterminate={multiSelect.isIndeterminate}
          slotProps={{ input: { 'aria-label': 'Select all rows' } }}
          onChange={() => multiSelect.toggleAll()}
          onClick={(event) => event.stopPropagation()}
          sx={{ ...multiSelectCheckboxSx, p: 0 }}
        />
      ),
      renderCell: (row, rowIndex) => {
        const rowId = getRowId(row, rowIndex);

        return (
          <Checkbox
            size={'small'}
            checked={multiSelect.isItemSelected(rowId)}
            slotProps={{ input: { 'aria-label': 'Select row' } }}
            onChange={() => multiSelect.toggleItem(rowId)}
            onClick={(event) => event.stopPropagation()}
            onKeyDown={(event) => event.stopPropagation()}
            sx={multiSelectCheckboxSx}
          />
        );
      },
      size: MULTI_SELECT_COLUMN_SIZE,
      minSize: MULTI_SELECT_COLUMN_SIZE,
      maxSize: MULTI_SELECT_COLUMN_SIZE,
      align: 'center',
      headerAlign: 'center',
      enableResizing: false,
      sx: {
        '&.MuiTableCell-root:first-of-type': {
          px: 0,
        },
      },
      headerSx: {
        '&.MuiTableCell-root:first-of-type': {
          px: 0,
        },
      },
    };
  }, [getRowId, multiSelect, multiSelectCheckboxSx]);
  const tableColumnSettings = useMemo(
    () => (multiSelectColumn ? [multiSelectColumn, ...columns] : columns),
    [columns, multiSelectColumn]
  );
  const tableContainerRef = useRef<HTMLDivElement | null>(null);
  const tableRef = useRef<HTMLTableElement | null>(null);
  const tableHeadRef = useRef<HTMLTableSectionElement | null>(null);
  const verticalScrollbarTrackRef = useRef<HTMLDivElement | null>(null);
  const horizontalScrollbarTrackRef = useRef<HTMLDivElement | null>(null);
  const verticalScrollbarThumbRef = useRef<HTMLDivElement | null>(null);
  const horizontalScrollbarThumbRef = useRef<HTMLDivElement | null>(null);
  const columnSizingRef = useRef<ColumnSizingState>({});
  const columnSizingFrameRef = useRef<number | null>(null);
  const latestDragSizingRef = useRef<ColumnSizingState | null>(null);
  const scrollbarThumbFrameRef = useRef<number | null>(null);
  const columnLayoutSignatureRef = useRef(getColumnLayoutSignature(tableColumnSettings));
  const columnSizingStorageKeyRef = useRef<string>('');
  const hasUserColumnSizingRef = useRef(false);
  const [containerWidth, setContainerWidth] = useState(0);
  const [headerHeight, setHeaderHeight] = useState(0);
  const [columnSizing, setColumnSizing] = useState<ColumnSizingState>({});
  const [scrollbarVisibility, setScrollbarVisibility] = useState(DEFAULT_SCROLLBAR_VISIBILITY);
  const [resizingColumnId, setResizingColumnId] = useState<string | null>(null);

  const columnLayoutSignature = useMemo(
    () => getColumnLayoutSignature(tableColumnSettings),
    [tableColumnSettings]
  );
  const columnSizingStorageKey = useMemo(
    () => getStorageKey(location.pathname, tableStorageId),
    [location.pathname, tableStorageId]
  );

  const normalizedColumnSizing = useMemo(
    () => normalizeColumnSizing(tableColumnSettings, columnSizing),
    [columnSizing, tableColumnSettings]
  );
  // Compare with the same responsive defaults that the reset action restores.
  const hasModifiedColumnSizing = useMemo(
    () =>
      !areColumnSizingEqual(
        normalizedColumnSizing,
        getFilledColumnSizing(tableColumnSettings, containerWidth)
      ),
    [containerWidth, normalizedColumnSizing, tableColumnSettings]
  );
  const columnSizeVars = useMemo(
    () => getColumnSizeVars(tableColumnSettings, normalizedColumnSizing),
    [normalizedColumnSizing, tableColumnSettings]
  );
  const tableWidth = Math.ceil(getColumnTotalSize(tableColumnSettings, normalizedColumnSizing));
  // Keep this filler mounted at zero width so resizing never remounts table cells or redistributes real columns.
  const tableLayoutWidth = Math.max(tableWidth, containerWidth);
  const trailingFillColumnWidth = Math.max(containerWidth - tableWidth, 0);
  const tableSizeVars = useMemo(
    (): typeof columnSizeVars => ({
      ...columnSizeVars,
      [TRAILING_FILL_COLUMN_SIZE_CSS_VARIABLE]: trailingFillColumnWidth,
    }),
    [columnSizeVars, trailingFillColumnWidth]
  );

  const columnSettingsById = useMemo(() => {
    return new Map(tableColumnSettings.map((column) => [column.id, column]));
  }, [tableColumnSettings]);

  const tableColumnDefinitions = useMemo<ColumnDef<TData, unknown>[]>(() => {
    return tableColumnSettings.map(
      (column): ColumnDef<TData, unknown> => ({
        id: column.id,
        header: () => column.header,
        cell: (info) => column.renderCell(info.row.original, info.row.index),
        size: getColumnCurrentSize(column, normalizedColumnSizing),
        minSize: getColumnMinSize(column),
        maxSize: getColumnMaxSize(column, getColumnMinSize(column)),
        enableResizing: column.enableResizing ?? true,
      })
    );
  }, [normalizedColumnSizing, tableColumnSettings]);

  /** Applies column widths against the current viewport, including any scrollbar gutter. */
  const applyColumnSizingStyles = useCallback(
    (nextColumnSizing: ColumnSizingState) => {
      const tableElement = tableRef.current;

      if (!tableElement) {
        return;
      }

      tableColumnSettings.forEach((column) => {
        const columnSize = getColumnCurrentSize(column, nextColumnSizing);

        tableElement.style.setProperty(`--header-${column.id}-size`, String(columnSize));
        tableElement.style.setProperty(`--col-${column.id}-size`, String(columnSize));
      });

      const nextTableWidth = Math.ceil(getColumnTotalSize(tableColumnSettings, nextColumnSizing));
      const viewportWidth = tableContainerRef.current?.clientWidth ?? 0;

      tableElement.style.width = `${Math.max(nextTableWidth, viewportWidth)}px`;
      tableElement.style.setProperty(
        TRAILING_FILL_COLUMN_SIZE_CSS_VARIABLE,
        String(Math.max(viewportWidth - nextTableWidth, 0))
      );
    },
    [tableColumnSettings]
  );

  const commitColumnSizing = useCallback(
    (
      nextColumnSizing: ColumnSizingState,
      options?: { persist?: boolean; hasUserColumnSizing?: boolean }
    ) => {
      const shouldPersist = options?.persist ?? false;
      const hasUserColumnSizing = options?.hasUserColumnSizing ?? hasUserColumnSizingRef.current;

      hasUserColumnSizingRef.current = hasUserColumnSizing;
      columnSizingRef.current = nextColumnSizing;
      applyColumnSizingStyles(nextColumnSizing);

      if (shouldPersist && typeof window !== 'undefined') {
        setStoredColumnSizing(
          columnSizingStorageKeyRef.current,
          columnLayoutSignatureRef.current,
          nextColumnSizing
        );
      }

      setColumnSizing((prevColumnSizing) =>
        areColumnSizingEqual(prevColumnSizing, nextColumnSizing)
          ? prevColumnSizing
          : nextColumnSizing
      );
    },
    [applyColumnSizingStyles]
  );

  const getCurrentScrollMetrics = useCallback((): ResizableTableScrollMetrics => {
    const tableContainerElement = tableContainerRef.current;

    if (!tableContainerElement) {
      return DEFAULT_SCROLL_METRICS;
    }

    return {
      scrollLeft: tableContainerElement.scrollLeft,
      scrollTop: tableContainerElement.scrollTop,
      scrollWidth: tableContainerElement.scrollWidth,
      scrollHeight: tableContainerElement.scrollHeight,
      clientWidth: tableContainerElement.clientWidth,
      clientHeight: tableContainerElement.clientHeight,
      verticalTrackLength: verticalScrollbarTrackRef.current?.clientHeight ?? 0,
      horizontalTrackLength: horizontalScrollbarTrackRef.current?.clientWidth ?? 0,
    };
  }, []);

  const applyScrollbarThumbStyles = useCallback(
    (nextScrollMetrics: ResizableTableScrollMetrics = getCurrentScrollMetrics()) => {
      const verticalThumbElement = verticalScrollbarThumbRef.current;
      const horizontalThumbElement = horizontalScrollbarThumbRef.current;

      if (verticalThumbElement) {
        const verticalThumbMetrics = getScrollbarThumbMetrics({
          contentSize: nextScrollMetrics.scrollHeight,
          scrollPosition: nextScrollMetrics.scrollTop,
          trackSize: nextScrollMetrics.verticalTrackLength,
          viewportSize: nextScrollMetrics.clientHeight,
        });

        verticalThumbElement.style.display = verticalThumbMetrics.size > 0 ? 'block' : 'none';
        verticalThumbElement.style.height = `${verticalThumbMetrics.size}px`;
        verticalThumbElement.style.transform = `translateY(${verticalThumbMetrics.offset}px)`;
      }

      if (horizontalThumbElement) {
        const horizontalThumbMetrics = getScrollbarThumbMetrics({
          contentSize: nextScrollMetrics.scrollWidth,
          scrollPosition: nextScrollMetrics.scrollLeft,
          trackSize: nextScrollMetrics.horizontalTrackLength,
          viewportSize: nextScrollMetrics.clientWidth,
        });

        horizontalThumbElement.style.display = horizontalThumbMetrics.size > 0 ? 'block' : 'none';
        horizontalThumbElement.style.width = `${horizontalThumbMetrics.size}px`;
        horizontalThumbElement.style.transform = `translateX(${horizontalThumbMetrics.offset}px)`;
      }
    },
    [getCurrentScrollMetrics]
  );

  const updateScrollbarVisibility = useCallback(() => {
    const nextScrollMetrics = getCurrentScrollMetrics();
    const nextScrollbarVisibility = {
      vertical: hasScrollableOverflow(
        nextScrollMetrics.scrollHeight,
        nextScrollMetrics.clientHeight
      ),
      horizontal: hasScrollableOverflow(
        nextScrollMetrics.scrollWidth,
        nextScrollMetrics.clientWidth
      ),
    };

    applyScrollbarThumbStyles(nextScrollMetrics);

    setScrollbarVisibility((prevScrollbarVisibility) =>
      prevScrollbarVisibility.vertical === nextScrollbarVisibility.vertical &&
      prevScrollbarVisibility.horizontal === nextScrollbarVisibility.horizontal
        ? prevScrollbarVisibility
        : nextScrollbarVisibility
    );
  }, [applyScrollbarThumbStyles, getCurrentScrollMetrics]);

  const scheduleScrollbarThumbUpdate = useCallback(() => {
    if (scrollbarThumbFrameRef.current !== null) {
      return;
    }

    scrollbarThumbFrameRef.current = window.requestAnimationFrame(() => {
      scrollbarThumbFrameRef.current = null;
      applyScrollbarThumbStyles();
    });
  }, [applyScrollbarThumbStyles]);

  /**
   * Settles column sizing before measuring overflow so scrollbar gutters never use stale widths.
   * Runs after every commit because rows, loading states, and subrows can change the geometry.
   */
  const synchronizeTableLayout = useCallback(() => {
    const tableContainerElement = tableContainerRef.current;

    if (!tableContainerElement) {
      return;
    }

    const viewportWidth = tableContainerElement.clientWidth;
    setContainerWidth((previousWidth) =>
      previousWidth === viewportWidth ? previousWidth : viewportWidth
    );

    if (columnLayoutSignatureRef.current !== columnLayoutSignature) {
      columnLayoutSignatureRef.current = columnLayoutSignature;
      columnSizingRef.current = normalizeColumnSizing(tableColumnSettings, columnSizingRef.current);
      latestDragSizingRef.current = null;
    }

    if (columnSizingStorageKeyRef.current !== columnSizingStorageKey) {
      columnSizingStorageKeyRef.current = columnSizingStorageKey;
      latestDragSizingRef.current = null;

      const storedColumnSizing = getStoredColumnSizing(
        columnSizingStorageKey,
        columnLayoutSignature
      );
      hasUserColumnSizingRef.current = storedColumnSizing !== null;
      columnSizingRef.current = normalizeColumnSizing(
        tableColumnSettings,
        storedColumnSizing ?? {}
      );
    }

    // A scrollbar appearing during a drag must not reset the in-progress manual column widths.
    const nextColumnSizing =
      hasUserColumnSizingRef.current || latestDragSizingRef.current !== null
        ? normalizeColumnSizing(tableColumnSettings, columnSizingRef.current)
        : getFilledColumnSizing(tableColumnSettings, viewportWidth);

    if (latestDragSizingRef.current !== null) {
      // Keep drag frames in the DOM; React receives the final widths when the pointer is released.
      applyColumnSizingStyles(nextColumnSizing);
    } else {
      commitColumnSizing(nextColumnSizing);
    }

    const nextHeaderHeight = Math.ceil(tableHeadRef.current?.getBoundingClientRect().height ?? 0);
    setHeaderHeight((previousHeight) =>
      previousHeight === nextHeaderHeight ? previousHeight : nextHeaderHeight
    );
    updateScrollbarVisibility();
  }, [
    applyColumnSizingStyles,
    columnLayoutSignature,
    columnSizingStorageKey,
    tableColumnSettings,
    commitColumnSizing,
    updateScrollbarVisibility,
  ]);

  useLayoutEffect(() => {
    // Settle row and column geometry before the next paint.
    synchronizeTableLayout();
  });

  useLayoutEffect(() => {
    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', synchronizeTableLayout);

      return () => {
        window.removeEventListener('resize', synchronizeTableLayout);
      };
    }

    // Container resizes and CSS-driven content changes use the same ordered layout pass.
    const resizeObserver = new ResizeObserver(synchronizeTableLayout);
    [tableContainerRef.current, tableRef.current, tableHeadRef.current].forEach((element) => {
      if (element) {
        resizeObserver.observe(element);
      }
    });

    return () => {
      resizeObserver.disconnect();
    };
  }, [synchronizeTableLayout]);

  useLayoutEffect(() => {
    return () => {
      if (columnSizingFrameRef.current !== null) {
        window.cancelAnimationFrame(columnSizingFrameRef.current);
      }

      if (scrollbarThumbFrameRef.current !== null) {
        window.cancelAnimationFrame(scrollbarThumbFrameRef.current);
      }
    };
  }, []);

  const startColumnResize = useCallback(
    (columnId: string, startClientX: number) => {
      const targetColumn = tableColumnSettings.find((column) => column.id === columnId);

      if (!targetColumn) {
        return;
      }

      const startColumnSizing = normalizeColumnSizing(tableColumnSettings, columnSizingRef.current);
      const startColumnSize = getColumnCurrentSize(targetColumn, startColumnSizing);
      const minSize = getColumnMinSize(targetColumn);
      const maxSize = getColumnMaxSize(targetColumn, minSize);
      const originalBodyCursor = document.body.style.cursor;
      const originalBodyUserSelect = document.body.style.userSelect;
      let pendingClientX = startClientX;

      latestDragSizingRef.current = startColumnSizing;
      columnSizingRef.current = startColumnSizing;
      setResizingColumnId(columnId);
      document.body.style.cursor = 'col-resize';
      document.body.style.userSelect = 'none';

      const applyPendingSize = () => {
        columnSizingFrameRef.current = null;

        const nextColumnSize = clampColumnSize(
          startColumnSize + pendingClientX - startClientX,
          minSize,
          maxSize
        );
        const nextColumnSizing = normalizeColumnSizing(tableColumnSettings, {
          ...startColumnSizing,
          [columnId]: nextColumnSize,
        });

        latestDragSizingRef.current = nextColumnSizing;
        columnSizingRef.current = nextColumnSizing;
        applyColumnSizingStyles(nextColumnSizing);
      };

      const schedulePendingSize = () => {
        if (columnSizingFrameRef.current !== null) {
          return;
        }

        columnSizingFrameRef.current = window.requestAnimationFrame(applyPendingSize);
      };

      const handlePointerMove = (event: PointerEvent) => {
        event.preventDefault();
        pendingClientX = event.clientX;
        schedulePendingSize();
      };

      const finishResize = () => {
        if (columnSizingFrameRef.current !== null) {
          window.cancelAnimationFrame(columnSizingFrameRef.current);
          applyPendingSize();
        }

        const finalColumnSizing = latestDragSizingRef.current;

        latestDragSizingRef.current = null;
        setResizingColumnId(null);
        document.body.style.cursor = originalBodyCursor;
        document.body.style.userSelect = originalBodyUserSelect;
        document.removeEventListener('pointermove', handlePointerMove);
        document.removeEventListener('pointerup', finishResize);
        document.removeEventListener('pointercancel', finishResize);

        if (finalColumnSizing) {
          commitColumnSizing(finalColumnSizing, {
            persist: true,
            hasUserColumnSizing: true,
          });
        }
      };

      document.addEventListener('pointermove', handlePointerMove);
      document.addEventListener('pointerup', finishResize);
      document.addEventListener('pointercancel', finishResize);
    },
    [applyColumnSizingStyles, commitColumnSizing, tableColumnSettings]
  );

  const resetColumnSize = useCallback(
    (columnId: string) => {
      const nextColumnSizing = { ...columnSizingRef.current };
      delete nextColumnSizing[columnId];

      commitColumnSizing(normalizeColumnSizing(tableColumnSettings, nextColumnSizing), {
        persist: true,
        hasUserColumnSizing: true,
      });
    },
    [commitColumnSizing, tableColumnSettings]
  );

  const resetTableColumnSizing = useCallback(() => {
    if (typeof window !== 'undefined') {
      window.localStorage.removeItem(columnSizingStorageKey);
    }

    hasUserColumnSizingRef.current = false;
    latestDragSizingRef.current = null;
    commitColumnSizing(getFilledColumnSizing(tableColumnSettings, containerWidth), {
      persist: false,
      hasUserColumnSizing: false,
    });
  }, [columnSizingStorageKey, commitColumnSizing, containerWidth, tableColumnSettings]);

  const scrollToPosition = useCallback(
    (axis: CustomScrollbarAxis, nextPosition: number) => {
      const tableContainerElement = tableContainerRef.current;

      if (!tableContainerElement) {
        return;
      }

      if (axis === 'vertical') {
        tableContainerElement.scrollTop = nextPosition;
      } else {
        tableContainerElement.scrollLeft = nextPosition;
      }

      scheduleScrollbarThumbUpdate();
    },
    [scheduleScrollbarThumbUpdate]
  );

  const handleCustomScrollbarWheel = useCallback(
    (event: React.WheelEvent<HTMLDivElement>) => {
      const tableContainerElement = tableContainerRef.current;

      if (!tableContainerElement) {
        return;
      }

      event.preventDefault();

      const maxScrollLeft = getScrollableDistance(
        tableContainerElement.scrollWidth,
        tableContainerElement.clientWidth
      );
      const maxScrollTop = getScrollableDistance(
        tableContainerElement.scrollHeight,
        tableContainerElement.clientHeight
      );

      tableContainerElement.scrollLeft = clampValue(
        tableContainerElement.scrollLeft + event.deltaX,
        0,
        maxScrollLeft
      );
      tableContainerElement.scrollTop = clampValue(
        tableContainerElement.scrollTop + event.deltaY,
        0,
        maxScrollTop
      );
      scheduleScrollbarThumbUpdate();
    },
    [scheduleScrollbarThumbUpdate]
  );

  const handleCustomScrollbarTrackPointerDown = useCallback(
    (axis: CustomScrollbarAxis, event: React.PointerEvent<HTMLDivElement>) => {
      if (event.button !== 0) {
        return;
      }

      const trackElement =
        axis === 'vertical'
          ? verticalScrollbarTrackRef.current
          : horizontalScrollbarTrackRef.current;

      if (!trackElement) {
        return;
      }

      event.preventDefault();

      const metrics = getCurrentScrollMetrics();
      const isVertical = axis === 'vertical';
      const viewportSize = isVertical ? metrics.clientHeight : metrics.clientWidth;
      const contentSize = isVertical ? metrics.scrollHeight : metrics.scrollWidth;
      const scrollPosition = isVertical ? metrics.scrollTop : metrics.scrollLeft;
      const trackSize = isVertical ? metrics.verticalTrackLength : metrics.horizontalTrackLength;
      const trackRect = trackElement.getBoundingClientRect();
      const pointerOffset = isVertical
        ? event.clientY - trackRect.top
        : event.clientX - trackRect.left;
      const thumbMetrics = getScrollbarThumbMetrics({
        contentSize,
        scrollPosition,
        trackSize,
        viewportSize,
      });

      scrollToPosition(
        axis,
        getScrollPositionFromTrackPointer({
          contentSize,
          pointerOffset,
          thumbSize: thumbMetrics.size,
          trackSize,
          viewportSize,
        })
      );
    },
    [getCurrentScrollMetrics, scrollToPosition]
  );

  const startCustomScrollbarDrag = useCallback(
    (axis: CustomScrollbarAxis, event: React.PointerEvent<HTMLDivElement>) => {
      if (event.button !== 0) {
        return;
      }

      const metrics = getCurrentScrollMetrics();
      const isVertical = axis === 'vertical';
      const viewportSize = isVertical ? metrics.clientHeight : metrics.clientWidth;
      const contentSize = isVertical ? metrics.scrollHeight : metrics.scrollWidth;
      const trackSize = isVertical ? metrics.verticalTrackLength : metrics.horizontalTrackLength;
      const startScrollPosition = isVertical ? metrics.scrollTop : metrics.scrollLeft;
      const thumbMetrics = getScrollbarThumbMetrics({
        contentSize,
        scrollPosition: startScrollPosition,
        trackSize,
        viewportSize,
      });
      const maxThumbOffset = Math.max(trackSize - thumbMetrics.size, 0);
      const maxScrollPosition = getScrollableDistance(contentSize, viewportSize);

      if (maxThumbOffset <= 0 || maxScrollPosition <= 0) {
        return;
      }

      event.preventDefault();
      event.stopPropagation();

      const startPointerPosition = isVertical ? event.clientY : event.clientX;
      const originalBodyUserSelect = document.body.style.userSelect;

      document.body.style.userSelect = 'none';

      const handlePointerMove = (pointerEvent: PointerEvent) => {
        pointerEvent.preventDefault();

        const pointerPosition = isVertical ? pointerEvent.clientY : pointerEvent.clientX;
        const nextScrollPosition = clampValue(
          startScrollPosition +
            ((pointerPosition - startPointerPosition) / maxThumbOffset) * maxScrollPosition,
          0,
          maxScrollPosition
        );

        scrollToPosition(axis, nextScrollPosition);
      };

      const finishDrag = () => {
        document.body.style.userSelect = originalBodyUserSelect;
        document.removeEventListener('pointermove', handlePointerMove);
        document.removeEventListener('pointerup', finishDrag);
        document.removeEventListener('pointercancel', finishDrag);
      };

      document.addEventListener('pointermove', handlePointerMove);
      document.addEventListener('pointerup', finishDrag);
      document.addEventListener('pointercancel', finishDrag);
    },
    [getCurrentScrollMetrics, scrollToPosition]
  );

  const table = useReactTable({
    data,
    columns: tableColumnDefinitions,
    getCoreRowModel: getCoreRowModel(),
    getRowId,
    columnResizeMode: 'onChange',
    enableColumnResizing: true,
    state: {
      columnSizing: normalizedColumnSizing,
    },
    onColumnSizingChange: (updater) => {
      const nextColumnSizing =
        typeof updater === 'function' ? updater(columnSizingRef.current) : updater;
      commitColumnSizing(normalizeColumnSizing(tableColumnSettings, nextColumnSizing), {
        persist: true,
        hasUserColumnSizing: true,
      });
    },
    defaultColumn: {
      size: DEFAULT_COLUMN_SIZE,
      minSize: DEFAULT_MIN_COLUMN_SIZE,
      maxSize: DEFAULT_MAX_COLUMN_SIZE,
    },
  });

  const visibleColumns = table.getVisibleLeafColumns();
  const colSpan = Math.max(visibleColumns.length, 1);
  const tableColSpan = colSpan + 1;
  const shouldRenderLoader = Boolean(infiniteLoader?.hasNextPage);
  const shouldRenderEmpty = !isLoading && data.length === 0;
  const shouldRenderVerticalScrollbar = scrollbarVisibility.vertical;
  const shouldRenderHorizontalScrollbar = scrollbarVisibility.horizontal;

  const { sx: paperSx, ...restPaperProps } = paperProps || {};
  const {
    onScroll: tableContainerOnScroll,
    sx: tableContainerSx,
    ...restTableContainerProps
  } = tableContainerProps || {};
  const { style: tableStyle, sx: tableSx, ...restTableProps } = tableProps || {};

  const renderCustomScrollbar = (axis: CustomScrollbarAxis) => {
    const isVertical = axis === 'vertical';

    return (
      <Box
        aria-hidden
        onWheel={handleCustomScrollbarWheel}
        sx={resizableTableScrollbarSlotSx(axis)}
      >
        <Box
          className={resizableTableScrollbarTrackClassName}
          ref={isVertical ? verticalScrollbarTrackRef : horizontalScrollbarTrackRef}
          onPointerDown={(event) => handleCustomScrollbarTrackPointerDown(axis, event)}
          sx={resizableTableScrollbarTrackSx(axis)}
        >
          <Box
            className={resizableTableScrollbarThumbClassName}
            ref={isVertical ? verticalScrollbarThumbRef : horizontalScrollbarThumbRef}
            onPointerDown={(event) => startCustomScrollbarDrag(axis, event)}
            sx={resizableTableScrollbarThumbSx(axis)}
          />
        </Box>
      </Box>
    );
  };

  return (
    <Paper {...restPaperProps} sx={resizableTablePaperSx(fillThePage, paperSx)}>
      <Box sx={resizableTableScrollLayoutSx(fillThePage)}>
        <Box sx={resizableTableScrollContentRowSx(fillThePage, headerHeight)}>
          <Box sx={resizableTableViewportSx(fillThePage)}>
            <MUITableContainer
              {...restTableContainerProps}
              ref={tableContainerRef}
              onScroll={(event) => {
                tableContainerOnScroll?.(event);
                scheduleScrollbarThumbUpdate();
              }}
              sx={resizableTableContainerSx(fillThePage, maxHeight, tableContainerSx)}
            >
              <Box aria-hidden sx={resizableTableHeadBackdropSx(headerHeight)} />

              <MUITable
                {...restTableProps}
                ref={tableRef}
                stickyHeader={stickyHeader}
                sx={resizableTableRootSx(tableLayoutWidth, tableSx)}
                style={{ ...tableStyle, ...tableSizeVars, width: tableLayoutWidth }}
              >
                <colgroup>
                  {visibleColumns.map((column) => (
                    <col
                      key={column.id}
                      style={{ width: `calc(var(--col-${column.id}-size) * 1px)` }}
                    />
                  ))}
                  <col style={{ width: trailingFillColumnWidthStyle }} />
                </colgroup>

                <MUITableHead ref={tableHeadRef} sx={resizableTableHeadSx}>
                  {table.getHeaderGroups().map((headerGroup, headerGroupIndex, headerGroups) => (
                    <MUITableRow key={headerGroup.id} sx={resizableTableHeadRowSx}>
                      {headerGroup.headers.map((header) => {
                        const columnSettings = columnSettingsById.get(header.column.id);
                        const hasResizeHandle = header.column.getCanResize();
                        const headerAlign =
                          columnSettings?.headerAlign ?? columnSettings?.align ?? 'center';
                        const headerContent = header.isPlaceholder
                          ? null
                          : flexRender(header.column.columnDef.header, header.getContext());

                        return (
                          <MUITableCell
                            key={header.id}
                            variant="head"
                            align={headerAlign}
                            colSpan={header.colSpan}
                            style={{
                              width: `calc(var(--header-${header.column.id}-size) * 1px)`,
                            }}
                            sx={mergeSx(resizableTableHeadCellSx(theme), columnSettings?.headerSx)}
                          >
                            <Box sx={resizableTableHeadContentSx(hasResizeHandle, headerAlign)}>
                              {renderHeaderContent(headerContent)}
                            </Box>
                            {hasResizeHandle && (
                              <Box
                                aria-hidden
                                onDoubleClick={(event) => {
                                  event.preventDefault();
                                  event.stopPropagation();
                                  resetColumnSize(header.column.id);
                                }}
                                onPointerDown={(event) => {
                                  event.preventDefault();
                                  event.stopPropagation();
                                  startColumnResize(header.column.id, event.clientX);
                                }}
                                sx={resizableTableResizeHandleSx(
                                  resizingColumnId === header.column.id
                                )}
                              />
                            )}
                          </MUITableCell>
                        );
                      })}
                      {headerGroupIndex === headerGroups.length - 1 && (
                        <MUITableCell
                          aria-hidden
                          variant="head"
                          style={{ width: trailingFillColumnWidthStyle }}
                          sx={mergeSx(resizableTableHeadCellSx(theme), { p: 0 })}
                        />
                      )}
                    </MUITableRow>
                  ))}
                </MUITableHead>

                <MUITableBody sx={resizableTableBodySx}>
                  {isLoading &&
                    Array.from({ length: Math.max(0, rowsSkeletonCount) }, (_, rowIndex) => (
                      <React.Fragment key={`skeleton-${rowIndex}`}>
                        <MUITableRow sx={resizableTableRowSx(theme, false)}>
                          {visibleColumns.map((column) => {
                            const columnSettings = columnSettingsById.get(column.id);

                            return (
                              <MUITableCell
                                key={column.id}
                                align={columnSettings?.align ?? 'center'}
                                style={{
                                  width: `calc(var(--col-${column.id}-size) * 1px)`,
                                }}
                                sx={mergeSx(resizableTableCellSx(theme), columnSettings?.sx)}
                              >
                                <Skeleton sx={{ height: skeletonHeight }} />
                              </MUITableCell>
                            );
                          })}
                          <MUITableCell
                            aria-hidden
                            style={{ width: trailingFillColumnWidthStyle }}
                            sx={mergeSx(resizableTableCellSx(theme), { p: 0 })}
                          />
                        </MUITableRow>

                        {withRowDividers && rowIndex < rowsSkeletonCount - 1 && (
                          <MUITableRow>
                            <MUITableCell colSpan={tableColSpan} sx={resizableTableDividerCellSx}>
                              <Box sx={resizableTableDividerSx} />
                            </MUITableCell>
                          </MUITableRow>
                        )}
                      </React.Fragment>
                    ))}

                  {!isLoading &&
                    table.getRowModel().rows.map((row, rowIndex) => {
                      const rowProps = getRowProps?.(row.original, rowIndex);
                      const rowLink = getRowLink?.(row.original, rowIndex);
                      const rowClick = rowLink ? undefined : onRowClick;
                      const linkCellId = row
                        .getVisibleCells()
                        .find((cell) => cell.column.id !== MULTI_SELECT_COLUMN_ID)?.id;
                      const subRow = renderSubRow?.(row.original, rowIndex);
                      const isLastRow = rowIndex === data.length - 1;

                      return (
                        <React.Fragment key={row.id}>
                          <MUITableRow
                            {...rowProps}
                            onClick={() => rowClick?.(row.original, rowIndex)}
                            onKeyDown={(event) => {
                              if (!rowClick || event.target !== event.currentTarget) {
                                return;
                              }

                              if (event.key === 'Enter' || event.key === ' ') {
                                event.preventDefault();
                                rowClick(row.original, rowIndex);
                              }
                            }}
                            role={rowClick ? 'button' : rowProps?.role}
                            tabIndex={rowClick ? 0 : rowProps?.tabIndex}
                            sx={mergeSx(
                              resizableTableRowSx(theme, Boolean(rowLink || rowClick)),
                              rowLink ? resizableTableLinkedRowSx : undefined,
                              rowProps?.sx
                            )}
                          >
                            {row.getVisibleCells().map((cell) => {
                              const columnSettings = columnSettingsById.get(cell.column.id);

                              return (
                                <MUITableCell
                                  key={cell.id}
                                  align={columnSettings?.align ?? 'center'}
                                  style={{
                                    width: `calc(var(--col-${cell.column.id}-size) * 1px)`,
                                  }}
                                  sx={mergeSx(resizableTableCellSx(theme), columnSettings?.sx)}
                                >
                                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                  {rowLink && cell.id === linkCellId && (
                                    <Box
                                      component={Link}
                                      {...rowLink}
                                      className="ResizableTable-rowLink"
                                      aria-label={rowLink['aria-label'] ?? `Open record ${row.id}`}
                                      onClick={(event) => {
                                        event.stopPropagation();
                                        rowLink.onClick?.(event);
                                      }}
                                      sx={resizableTableRowLinkSx}
                                    />
                                  )}
                                </MUITableCell>
                              );
                            })}
                            <MUITableCell
                              aria-hidden
                              style={{ width: trailingFillColumnWidthStyle }}
                              sx={mergeSx(resizableTableCellSx(theme), { p: 0 })}
                            />
                          </MUITableRow>

                          {subRow !== undefined && subRow !== null && (
                            <MUITableRow>
                              <MUITableCell
                                colSpan={tableColSpan}
                                sx={resizableTableFullWidthCellSx}
                              >
                                {pinSubRowToContainer ? (
                                  <Box sx={resizableTablePinnedSubRowContentSx(containerWidth)}>
                                    {subRow}
                                  </Box>
                                ) : (
                                  subRow
                                )}
                              </MUITableCell>
                            </MUITableRow>
                          )}

                          {withRowDividers && !isLastRow && (
                            <MUITableRow>
                              <MUITableCell colSpan={tableColSpan} sx={resizableTableDividerCellSx}>
                                <Box sx={resizableTableDividerSx} />
                              </MUITableCell>
                            </MUITableRow>
                          )}
                        </React.Fragment>
                      );
                    })}

                  {shouldRenderLoader && (
                    <MUITableRow ref={infiniteLoader?.ref}>
                      <MUITableCell
                        align="center"
                        colSpan={tableColSpan}
                        sx={resizableTableLoaderCellSx}
                      >
                        <Loader size="small" color="accent" />
                      </MUITableCell>
                    </MUITableRow>
                  )}
                </MUITableBody>
              </MUITable>

              {shouldRenderEmpty && (
                <Box sx={resizableTableEmptyStateSx(fillThePage, headerHeight)}>
                  {emptyNode ?? (
                    <Typography variant="body_14_24" color="inherit">
                      No records found
                    </Typography>
                  )}
                </Box>
              )}
            </MUITableContainer>
          </Box>

          <Box sx={resizableTableVerticalScrollbarColumnSx(fillThePage, headerHeight)}>
            {shouldRenderVerticalScrollbar && renderCustomScrollbar('vertical')}
          </Box>
        </Box>

        {shouldRenderHorizontalScrollbar && (
          <Box sx={resizableTableScrollbarFooterSx()}>
            {renderCustomScrollbar('horizontal')}
            <Box sx={resizableTableScrollbarCornerSx()} />
          </Box>
        )}
      </Box>

      {hasModifiedColumnSizing && (
        <Box sx={{ position: 'absolute', right: 12, bottom: 12, zIndex: 6 }}>
          <IconButton
            aria-label="Reset table column widths"
            size="medium"
            onClick={resetTableColumnSizing}
          >
            <RefreshIcon />
          </IconButton>
        </Box>
      )}
    </Paper>
  );
};
